import { axiosApiClient } from "../index";
import {
  updateLocalProgress,
  clearLocalProgress,
} from "../../store/slices/lessonProgress/slice";
import {
  updateLessonProgress as updateLessonProgressThunk,
  completeLesson as completeLessonThunk,
} from "../../store/slices/lessonProgress/asyncThunks";

const LOCAL_PROGRESS_KEY_PREFIX = "lesson_progress_";

// Get all lessons for a course with user progress
export const getCourseLessons = async (courseId) => {
  try {
    const response = await axiosApiClient.get(
      `/lesson-progress/course/${courseId}`,
    );
    return response.data;
  } catch (error) {
    const err = new Error(
      error.response?.data?.message || "Greška prilikom dohvatanja lekcija",
    );
    if (error.response?.data?.expired) {
      err.expired = true;
      err.expiryDate = error.response.data.expiryDate;
    } else if (error.response?.status === 403) {
      err.accessDenied = true;
    }
    throw err;
  }
};

// Get specific lesson with progress
export const getLesson = async (lessonId) => {
  try {
    const response = await axiosApiClient.get(
      `/lesson-progress/lesson/${lessonId}`,
    );
    return response.data;
  } catch (error) {
    const err = new Error(
      error.response?.data?.message || "Greška prilikom dohvatanja lekcije",
    );
    if (error.response?.data?.expired) {
      err.expired = true;
      err.expiryDate = error.response.data.expiryDate;
    } else if (error.response?.status === 403) {
      err.accessDenied = true;
    }
    throw err;
  }
};

// Update lesson progress
export const updateProgress = async (lessonId, progressData) => {
  try {
    const response = await axiosApiClient.put(
      `/lesson-progress/${lessonId}`,
      progressData,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Greška prilikom ažuriranja progresa",
    );
  }
};

// Check certificate eligibility
export const checkCertificateEligibility = async (courseId) => {
  try {
    const response = await axiosApiClient.get(
      `/lesson-progress/certificate-eligibility/${courseId}`,
    );
    return response.data;
  } catch (error) {
    const err = new Error(
      error.response?.data?.message ||
        "Greška prilikom provere mogućnosti aplikacije za sertifikat",
    );
    if (error.response?.data?.expired) {
      err.expired = true;
      err.expiryDate = error.response.data.expiryDate;
    } else if (error.response?.status === 403) {
      err.accessDenied = true;
    }
    throw err;
  }
};

// ============================================================
// localStorage helpers for offline progress backup
// ============================================================

// Da li je lokalni snimak ispred serverskog napretka
// (noviji po vremenu ili dalja pozicija)?
export const isLocalProgressAhead = (localSaved, serverProgress) => {
  if (!localSaved || !(localSaved.currentTime > 0)) {
    return false;
  }
  if (!serverProgress) {
    return true;
  }

  const serverTimestamp = serverProgress.lastWatchedAt
    ? new Date(serverProgress.lastWatchedAt).getTime()
    : 0;
  const localIsNewer = (localSaved.timestamp || 0) > serverTimestamp;
  const localIsFurther =
    localSaved.currentTime > (serverProgress.watchedDuration || 0);

  return localIsNewer || localIsFurther;
};

// Obriši sve lokalne snimke napretka (poziva se pri odjavi)
export const clearAllLocalProgress = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LOCAL_PROGRESS_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Error clearing local lesson progress:", error);
  }
};

// ============================================================
// Progress manager class for auto-saving with Redux integration
// ============================================================
export class ProgressManager {
  constructor(
    lessonId,
    dispatch,
    onProgressUpdate = null,
    onError = null,
    userId = null,
  ) {
    this.lessonId = lessonId;
    this.dispatch = dispatch;
    this.onProgressUpdate = onProgressUpdate;
    this.onError = onError;
    this.userId = userId;

    // Progress tracking
    this.currentTime = 0;
    this.duration = 0;
    this.lastSavedTime = 0;
    this.lastLocalSaveTime = 0;
    this.pendingProgress = null;

    // Timers
    this.serverSaveTimer = null;

    // Configuration
    // [FIX #1] Server save svakih 30s umesto 60s, koristi setInterval
    this.SERVER_SAVE_INTERVAL = 30000; // 30 sekundi
    this.LOCAL_SAVE_INTERVAL = 5000; // localStorage/Redux najviše na 5s
    this.MIN_PROGRESS_DIFF = 3; // Minimum 3 sekunde razlike da bi se save desio

    // [FIX #2] Samo localStorage na unload — nema sendBeacon bez auth tokena
    this.setupUnloadHandler();
  }

  // Ključ vezan za korisnika da se napredak ne meša između naloga
  get storageKey() {
    return `${LOCAL_PROGRESS_KEY_PREFIX}${this.userId || "anon"}_${this.lessonId}`;
  }

  // --------------------------------------------------------
  // [FIX #5] Pozovi ovo iz komponente pri mount-u da se
  //          localStorage progres sync-uje sa serverom.
  //          Snimak se šalje samo ako je ispred serverskog.
  // --------------------------------------------------------
  async recoverFromLocalStorage(serverProgress = null) {
    const saved = this.loadFromLocalStorage();
    if (!saved || !(saved.currentTime > 0)) {
      return null;
    }

    if (!isLocalProgressAhead(saved, serverProgress)) {
      // Serverski napredak je noviji — lokalni snimak je zastareo
      this.clearLocalStorage();
      return null;
    }

    this.currentTime = saved.currentTime;
    this.duration = saved.duration;
    try {
      await this.saveToServer();
    } catch (error) {
      console.error("Failed to sync localStorage progress:", error);
    }
    return saved;
  }

  // Update current progress (called frequently from video player)
  updateProgress(currentTime, duration) {
    this.currentTime = currentTime;
    this.duration = duration;

    // Throttle: localStorage upis i Redux update najviše jednom u 5s
    const now = Date.now();
    if (now - this.lastLocalSaveTime >= this.LOCAL_SAVE_INTERVAL) {
      this.lastLocalSaveTime = now;

      // Save to localStorage (backup)
      this.saveToLocalStorage();

      // Update Redux local state
      if (this.dispatch) {
        this.dispatch(
          updateLocalProgress({
            lessonId: this.lessonId,
            progress: {
              watchedDuration: currentTime,
              progressPercentage:
                duration > 0 ? (currentTime / duration) * 100 : 0,
              lastWatchedAt: new Date().toISOString(),
            },
          }),
        );
      }
    }

    // [FIX #1] Pokreni auto-save interval JEDNOM (ne resetuj svaki put)
    this.startAutoSave();
  }

  // --------------------------------------------------
  // [FIX #1] Zamena za stari scheduleServerSave()
  // setInterval se pokreće jednom i čuva svakih 30s
  // --------------------------------------------------
  startAutoSave() {
    // Ako je interval već pokrenut, ne pokreći ponovo
    if (this.serverSaveTimer) return;

    this.serverSaveTimer = setInterval(() => {
      this.saveToServer().catch(() => {});
    }, this.SERVER_SAVE_INTERVAL);
  }

  // Save progress to localStorage
  saveToLocalStorage() {
    const progressData = {
      lessonId: this.lessonId,
      currentTime: this.currentTime,
      duration: this.duration,
      timestamp: Date.now(),
    };

    localStorage.setItem(this.storageKey, JSON.stringify(progressData));
  }

  // Load progress from localStorage
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const data = JSON.parse(saved);
        // Only use if saved within last 24 hours
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return {
            currentTime: data.currentTime,
            duration: data.duration,
            timestamp: data.timestamp,
          };
        }
      }
    } catch (error) {
      console.error("Error loading progress from localStorage:", error);
    }
    return null;
  }

  // Remove progress snapshot from localStorage
  clearLocalStorage() {
    localStorage.removeItem(this.storageKey);
  }

  // Save progress to server using Redux
  async saveToServer(forceComplete = false) {
    try {
      const timeDiff = Math.abs(this.currentTime - this.lastSavedTime);

      // Skip if no significant progress and not forced
      if (!forceComplete && timeDiff < this.MIN_PROGRESS_DIFF) {
        return;
      }

      // [FIX #4] Zaokruži na integer pre slanja — baza je INTEGER
      const progressData = {
        currentTime: Math.floor(this.currentTime),
        duration: Math.floor(this.duration),
        forceComplete,
      };

      // Use Redux thunk instead of direct API call
      if (this.dispatch) {
        const result = await this.dispatch(
          updateLessonProgressThunk({
            lessonId: this.lessonId,
            progressData,
          }),
        ).unwrap();

        this.lastSavedTime = this.currentTime;

        // Clear localStorage on successful save
        this.clearLocalStorage();

        // Clear Redux local progress
        this.dispatch(clearLocalProgress(this.lessonId));

        // Notify parent component
        if (this.onProgressUpdate && result.success) {
          this.onProgressUpdate(result);
        }

        return result;
      } else {
        // Fallback to direct API call if no dispatch
        const response = await updateProgress(this.lessonId, progressData);

        this.lastSavedTime = this.currentTime;
        this.clearLocalStorage();

        if (this.onProgressUpdate && response.success) {
          this.onProgressUpdate(response);
        }

        return response;
      }
    } catch (error) {
      console.error("Error saving progress to server:", error);

      // Store failed attempt for retry
      this.pendingProgress = {
        currentTime: Math.floor(this.currentTime),
        duration: Math.floor(this.duration),
        forceComplete,
      };

      if (this.onError) {
        this.onError(error);
      }

      throw error;
    }
  }

  // Force save immediately (called on pause, lesson switch, etc.)
  async forceSave() {
    // Stop auto-save interval
    if (this.serverSaveTimer) {
      clearInterval(this.serverSaveTimer);
      this.serverSaveTimer = null;
    }

    // Save current progress
    if (this.currentTime > 0) {
      try {
        await this.saveToServer();
      } catch (error) {
        // On error, ensure localStorage is updated as backup
        this.saveToLocalStorage();
      }
    }

    // Retry pending progress if exists
    if (this.pendingProgress) {
      try {
        if (this.dispatch) {
          await this.dispatch(
            updateLessonProgressThunk({
              lessonId: this.lessonId,
              progressData: this.pendingProgress,
            }),
          ).unwrap();
        } else {
          await updateProgress(this.lessonId, this.pendingProgress);
        }
        this.pendingProgress = null;
      } catch (error) {
        console.error("Failed to save pending progress:", error);
      }
    }
  }

  // Complete lesson
  async completeLesson() {
    try {
      if (this.dispatch) {
        return await this.dispatch(
          completeLessonThunk({
            lessonId: this.lessonId,
            currentTime: Math.floor(this.currentTime),
            duration: Math.floor(this.duration),
          }),
        ).unwrap();
      } else {
        return await this.saveToServer(true);
      }
    } catch (error) {
      console.error("Error completing lesson:", error);
      throw error;
    }
  }

  // --------------------------------------------------
  // [FIX #2] Setup page unload handler
  // Samo localStorage — sendBeacon ne radi bez auth tokena
  // --------------------------------------------------
  setupUnloadHandler() {
    const handleUnload = () => {
      // Sačuvaj u localStorage — biće sync-ovano pri sledećem učitavanju
      if (this.currentTime > 0) {
        this.saveToLocalStorage();
      }
    };

    window.addEventListener("beforeunload", handleUnload);

    // Store reference for cleanup
    this.unloadHandler = handleUnload;
  }

  // Cleanup — pozovi kad se komponenta unmount-uje
  destroy() {
    // [FIX #1] Clear interval (ne timeout)
    if (this.serverSaveTimer) {
      clearInterval(this.serverSaveTimer);
      this.serverSaveTimer = null;
    }

    // Remove event listeners
    if (this.unloadHandler) {
      window.removeEventListener("beforeunload", this.unloadHandler);
    }

    // Force save before cleanup
    this.forceSave();
  }
}
